/**
 * Owner: Masa Bayu
 * File: spacing.ts
 * Date:Time: 2026-10-14 10:38
 * Description: Q4 word-spacing fast path ≤0.5pt · else wordStarts primary.
 */

import type { SpacingInput, SpacingOutput } from './types.js';

/** Q4 · G31 — uniform CSS word-spacing allowed only at/under this deviation. */
export const FAST_PATH_MAX_DEVIATION_PT = 0.5;

function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}

function maxAbsDeviation(values: readonly number[], center: number): number {
  let max = 0;
  for (const v of values) {
    const d = Math.abs(v - center);
    if (d > max) max = d;
  }
  return max;
}

/**
 * Derive inter-word gaps from wordStarts when wordGaps absent.
 * Requires equal token count: starts length = words. Gaps cannot be
 * recovered without glyph widths — returns [] (forces primary path).
 */
function gapsFromStartsOnly(_wordStarts: readonly number[]): readonly number[] {
  return [];
}

/**
 * Compute uniform word-spacing candidate and Q4 fast-path eligibility.
 * Fail-closed: bboxWidth ≤ 0 → no fast path.
 * CJK / empty gaps → skip fast path (char / wordStarts primary).
 */
export function computeWordSpacing(input: SpacingInput): SpacingOutput {
  const fail: SpacingOutput = {
    wordSpacingPt: 0,
    fastPathUsed: false,
    deviationPt: Number.POSITIVE_INFINITY,
  };

  if (!(input.bboxWidth > 0)) {
    return fail;
  }

  if (input.script === 'cjk') {
    return {
      wordSpacingPt: 0,
      fastPathUsed: false,
      deviationPt: 0,
    };
  }

  const gaps =
    input.wordGaps !== undefined
      ? input.wordGaps
      : input.wordStarts !== undefined
        ? gapsFromStartsOnly(input.wordStarts)
        : [];

  if (gaps.length === 0) {
    // wordGaps absent / CJK empty → wordStarts primary (Q4)
    return {
      wordSpacingPt: 0,
      fastPathUsed: false,
      deviationPt: input.wordStarts !== undefined && input.wordStarts.length > 0 ? 0 : 0,
    };
  }

  const uniform = mean(gaps);
  const deviationPt = maxAbsDeviation(gaps, uniform);
  const fastPathUsed = deviationPt <= FAST_PATH_MAX_DEVIATION_PT;

  return {
    wordSpacingPt: uniform,
    fastPathUsed,
    deviationPt,
  };
}
